import { useState } from 'react';
import { useOutletContext } from 'react-router';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import {
  ClipboardCopyIcon,
  CopyIcon,
  DownloadIcon,
  MoreVerticalIcon,
  PencilIcon,
  Trash2Icon,
} from 'lucide-react';
import {
  downloadBriefMarkdown,
  useDeleteBrief,
  useDuplicateBrief,
  useExportBriefPdf,
  type Brief,
} from '@/entities/brief';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { EditBriefModal } from '@/features/edit-brief';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { ScopedPermission } from '@/shared/ui';
import type { ProjectDetailOutletContext } from '../../../../projectDetail.types';
import { useTranslation } from 'react-i18next';

interface BriefsActionMenuProps {
  /**
   * The brief this menu acts on. The full object (rather than just id/title) is
   * required so "Copy as Markdown" can serialize the brief's sections
   * client-side; `id`/`title` are derived from it. The scoped mutations use the
   * `platformProjectId` read from the ProjectDetail outlet context, not
   * `brief.projectId` (which is the internal id and 403s against the
   * `projects/:platformProjectId/*` routes).
   */
  brief: Brief;
}

/**
 * Row action menu for a Brief, per the Figma data-table design: Edit,
 * Duplicate, Download PDF, Download Markdown, Delete. (Figma's "Organize" item
 * is deferred until the move-to-folder feature lands — only implemented
 * actions are listed.)
 *
 * Edit opens the rename dialog (`EditBriefModal`). Duplicate clones the brief
 * via `useDuplicateBrief` (server-side copy with " Copy" appended). Download
 * PDF exports via `useExportBriefPdf`. Download Markdown serializes the brief's
 * sections to Markdown and downloads them as `{briefTitle}.md`. Delete opens a
 * confirmation modal and calls `useDeleteBrief` on confirm.
 *
 * The trigger stops click propagation so the surrounding row's navigation
 * handler does NOT fire when the user opens the menu.
 *
 * Keyboard a11y: the trigger is a focusable `<Button>`, the menu is
 * Radix-backed via monks-ui — Tab/Enter/Space open it, Escape closes it
 * and returns focus to the trigger.
 */
export function BriefsActionMenu(props: BriefsActionMenuProps) {
  const { t } = useTranslation('projects');
  const { t: tCommon } = useTranslation('common');

  const { brief } = props;
  const { id: briefId, title: briefTitle } = brief;
  const { project } = useOutletContext<ProjectDetailOutletContext>();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);

  const duplicateMutation = useDuplicateBrief({ projectId: project.platformProjectId, briefId });
  const deleteMutation = useDeleteBrief({ projectId: project.platformProjectId, briefId });
  const { exportPdf, isExporting } = useExportBriefPdf({
    projectId: project.platformProjectId,
    briefId,
    briefTitle,
  });

  const handleEdit = () => {
    setIsRenameOpen(true);
  };

  const handleDuplicate = () => {
    if (duplicateMutation.isPending) return;
    duplicateMutation.mutate();
  };

  const handleDownloadPdf = () => {
    if (isExporting) return;
    void exportPdf();
  };

  // Serializes the brief to Markdown and downloads it as `{briefTitle}.md`.
  const handleDownloadMarkdown = () => {
    downloadBriefMarkdown(brief);
  };

  const handleOpenDelete = () => {
    setIsDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteOpen(false);
  };

  const handleConfirmDelete = () => {
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        setIsDeleteOpen(false);
      },
    });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('projectDetail.briefsActionMenu.briefActions')}
            data-brief-id={briefId}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVerticalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectEditDetails]}>
            <DropdownMenuItem onSelect={handleEdit}>
              <PencilIcon className="mr-2 size-4" />
              {t('projectDetail.briefsActionMenu.edit')}
            </DropdownMenuItem>
          </ScopedPermission>
          <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectEditDetails]}>
            <DropdownMenuItem onSelect={handleDuplicate} disabled={duplicateMutation.isPending}>
              <CopyIcon className="mr-2 size-4" />
              {t('projectDetail.briefsActionMenu.duplicate')}
            </DropdownMenuItem>
          </ScopedPermission>
          <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectView]}>
            <DropdownMenuItem onSelect={handleDownloadPdf} disabled={isExporting}>
              <DownloadIcon className="mr-2 size-4" />
              {isExporting
                ? t('projectDetail.briefsActionMenu.preparingPdf')
                : t('projectDetail.briefsActionMenu.downloadPdf')}
            </DropdownMenuItem>
          </ScopedPermission>
          <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectView]}>
            <DropdownMenuItem onSelect={handleDownloadMarkdown}>
              <ClipboardCopyIcon className="mr-2 size-4" />
              {t('projectDetail.briefsActionMenu.downloadMarkdown')}
            </DropdownMenuItem>
          </ScopedPermission>
          <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectEditDetails]}>
            <DropdownMenuItem onSelect={handleOpenDelete}>
              <Trash2Icon className="mr-2 size-4" />
              {t('projectDetail.briefsActionMenu.delete')}
            </DropdownMenuItem>
          </ScopedPermission>
        </DropdownMenuContent>
      </DropdownMenu>
      {/* Radix Dialog portals to body, but React SyntheticEvents bubble
          through the React tree — without this wrapper, the confirm-button
          click reaches the parent <TableRow onClick> and navigates. The
          wrapper is structural; keyboard interaction lives on the dialog
          itself (Esc closes, Enter confirms via the focused button). */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={(e) => e.stopPropagation()}>
        <DeleteConfirmationModal
          open={isDeleteOpen}
          isDeleting={deleteMutation.isPending}
          onCancel={handleCancelDelete}
          onConfirmDelete={handleConfirmDelete}
          resourceType={tCommon('components.deleteConfirmationModal.brief')}
          resourceName={briefTitle}
          warningMessage={t('projectDetail.briefsActionMenu.thisActionCannotBeUndone')}
          data-testid="brief-delete-modal"
        />
        <EditBriefModal
          open={isRenameOpen}
          projectId={project.platformProjectId}
          briefId={briefId}
          currentTitle={briefTitle}
          onClose={() => setIsRenameOpen(false)}
        />
      </div>
    </>
  );
}
